import {
  NavLink,
  Navbar as MantineNavBar,
  ScrollArea,
  Divider,
  Skeleton,
  Group,
  Stack,
  Text,
  Badge,
  Tooltip,
} from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import {
  Home,
  List,
  Help,
  FileSettings,
  Settings,
  Users,
  FolderOff,
  ClipboardList,
  Dashboard,
  Folder,
  LayoutGrid,
  Timeline,
} from 'tabler-icons-react';

import { useGetProjects } from 'api/projects/getProjects';
import UserProfile from 'features/user/components/UserProfile';
import { User } from 'types';

import styles from './Navbar.module.css';

type NavbarProps = {
  onClose: () => void;
  user: User;
};

function Navbar({ onClose, user }: NavbarProps) {
  const location = useLocation();
  const { t } = useTranslation();

  const {
    data = {
      projects: [],
      createdIssuesLast7Days: [],
      completedIssuesLast7Days: [],
    },
    isLoading,
    isSuccess,
  } = useGetProjects(user.org?._id);

  return (
    <>
      <MantineNavBar.Section grow component={ScrollArea}>
        <NavLink
          icon={<Home />}
          label={t('nav.overview')}
          component={Link}
          to="/dashboard"
          active={location.pathname === '/dashboard' || (location.pathname.startsWith('/dashboard/') && !location.pathname.startsWith('/dashboards'))}
          mb={15}
          onClick={onClose}
          color="violet.7"
          className={styles.link}
        />
        <NavLink
          icon={<ClipboardList />}
          label={t('nav.myTasks')}
          component={Link}
          to="/my-tasks"
          active={location.pathname === '/my-tasks'}
          mb={15}
          onClick={onClose}
          color="violet.7"
          className={styles.link}
        />
        <NavLink
          icon={<Dashboard />}
          label={t('nav.customDashboards')}
          component={Link}
          to="/dashboards"
          active={location.pathname === '/dashboards'}
          mb={15}
          onClick={onClose}
          color="violet.7"
          className={styles.link}
        />
        <NavLink
          icon={<Users />}
          label={t('nav.teamActivity')}
          component={Link}
          to="/team-activity"
          active={location.pathname === '/team-activity'}
          mb={15}
          onClick={onClose}
          color="violet.7"
          className={styles.link}
        />
        <NavLink
          icon={<LayoutGrid />}
          label={t('nav.portfolio')}
          component={Link}
          to="/portfolio"
          active={location.pathname === '/portfolio'}
          mb={15}
          onClick={onClose}
          color="violet.7"
          className={styles.link}
        />
        <NavLink
          icon={<Timeline />}
          label={t('nav.crossProjectRoadmap')}
          component={Link}
          to="/cross-project-roadmap"
          active={location.pathname === '/cross-project-roadmap'}
          mb={15}
          onClick={onClose}
          color="violet.7"
          className={styles.link}
        />
        <NavLink
          icon={<Users />}
          label={t('nav.resourceView')}
          component={Link}
          to="/resource-view"
          active={location.pathname === '/resource-view'}
          mb={15}
          onClick={onClose}
          color="violet.7"
          className={styles.link}
        />
        <NavLink
          icon={<Help />}
          label={t('nav.help')}
          component={Link}
          to="/help"
          active={location.pathname === '/help'}
          mb={15}
          onClick={onClose}
          color="violet.7"
          className={styles.link}
        />
        <NavLink
          icon={<List />}
          label={
            <Group spacing={8} noWrap>
              <Text>{t('nav.projects')}</Text>
              {data.projects.length > 0 && (
                <Badge size="xs" variant="light" color="violet">
                  {data.projects.length}
                </Badge>
              )}
            </Group>
          }
          childrenOffset={28}
          className={styles.link}
        >
          <ScrollArea.Autosize
            maxHeight={300}
            scrollbarSize={8}
            placeholder=""
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          >
            <Stack spacing={4} py={4}>
              {data.projects.map((project) => {
                const totalIssues =
                  (project.todoIssues?.length || 0) +
                  (project.inProgressIssues?.length || 0) +
                  (project.inReviewIssues?.length || 0) +
                  (project.deploymentIssues?.length || 0) +
                  (project.blockedIssues?.length || 0) +
                  (project.completedIssues?.length || 0);
                const isActive = location.pathname === `/projects/${project._id}`;

                return (
                  <Tooltip
                    key={project._id}
                    label={project.description || project.name}
                    position="right"
                    withArrow
                    disabled={!project.description}
                  >
                    <NavLink
                      icon={<Folder size={16} />}
                      label={
                        <Group spacing={8} noWrap position="apart" style={{ flex: 1 }}>
                          <Group spacing={6} noWrap style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" weight={isActive ? 600 : 400} truncate>
                              {project.name}
                            </Text>
                            {project.key && (
                              <Badge
                                size="xs"
                                variant="light"
                                color="violet"
                                style={{ flexShrink: 0 }}
                              >
                                {project.key}
                              </Badge>
                            )}
                          </Group>
                          {totalIssues > 0 && (
                            <Badge
                              size="xs"
                              variant="dot"
                              color="gray"
                              style={{ flexShrink: 0 }}
                            >
                              {totalIssues}
                            </Badge>
                          )}
                        </Group>
                      }
                      component={Link}
                      to={`/projects/${project._id}`}
                      active={isActive}
                      onClick={onClose}
                      color="violet.7"
                      className={styles.link}
                      sx={(theme) => {
                        let backgroundColor = 'transparent';
                        if (isActive) {
                          backgroundColor =
                            theme.colorScheme === 'dark'
                              ? theme.colors.dark[7]
                              : theme.colors.violet[0];
                        }

                        return {
                          borderLeft: `2px solid ${
                            isActive ? theme.colors.violet[6] : theme.colors.gray[3]
                          }`,
                          paddingLeft: 12,
                          paddingRight: 8,
                          paddingTop: 8,
                          paddingBottom: 8,
                          borderRadius: 4,
                          marginLeft: 4,
                          marginRight: 4,
                          marginBottom: 2,
                          '&:hover': {
                            backgroundColor:
                              theme.colorScheme === 'dark'
                                ? theme.colors.dark[6]
                                : theme.colors.gray[0],
                          },
                          backgroundColor,
                        };
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
            {isLoading && (
              <Stack spacing={4} py={4} px={4}>
                {[1, 2, 3].map((i) => (
                  <Group
                    key={i}
                    spacing={12}
                    px={12}
                    py={8}
                    sx={(theme) => ({
                      borderLeft: `2px solid ${theme.colors.gray[3]}`,
                      borderRadius: 4,
                      marginLeft: 4,
                      marginRight: 4,
                    })}
                  >
                    <Skeleton circle height={16} width={16} />
                    <Skeleton height={12} width={120} radius="xl" />
                    <Skeleton height={12} width={40} radius="xl" ml="auto" />
                  </Group>
                ))}
              </Stack>
            )}
            {isSuccess && data.projects.length === 0 && (
              <Stack align="center" spacing={12} py={20} px={16}>
                <FolderOff size={24} color="#868E96" />
                <Text align="center" size={13} color="dimmed" weight={500}>
                  {t('nav.noProjectsYet')}
                </Text>
                <Text align="center" size={12} color="dimmed">
                  {t('nav.createFirstProject')}
                </Text>
              </Stack>
            )}
          </ScrollArea.Autosize>
        </NavLink>
        {(user.role === 'admin' ||
          user.role === 'project manager' ||
          user.role === 'super admin') && (
          <NavLink
            icon={<FileSettings />}
            label={t('nav.projectManagement')}
            component={Link}
            to="/project-management"
            active={location.pathname === '/project-management'}
            my={15}
            onClick={onClose}
            color="violet.7"
            className={styles.link}
          />
        )}
        {(user.role === 'admin' || user.role === 'super admin') && (
          <NavLink
            icon={<Settings />}
            label={t('nav.adminSettings')}
            component={Link}
            to="/admin-settings"
            active={location.pathname === '/admin-settings'}
            onClick={onClose}
            color="violet.7"
            className={styles.link}
          />
        )}
      </MantineNavBar.Section>
      <Divider my={15} />
      <MantineNavBar.Section>
        <UserProfile user={user} />
      </MantineNavBar.Section>
    </>
  );
}

export default Navbar;
